// frozen_string_literal: true

import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="mark-result-form"
export default class extends Controller {
  static targets = [
    "accountSearch",
    "accountList",
    "accountItem",
    "accountCheckbox",
    "accountCount",
    "status",
    "createdAt",
    "submitButton",
    "quantityUnit",
  ];

  connect() {
    console.log("MarkResultForm controller connected");
    this.updateAccountCount();
    this.updateQuantityUnitLabel();
    this.searchTimeout = null;
  }

  updateQuantityUnitLabel() {
    const taskInput = document.querySelector(
      'input[name="mark_result[task_id]"]',
    );
    const unitMap = JSON.parse(taskInput?.dataset?.unitMap || "{}");
    const unitLabel = unitMap[taskInput?.value] || "按任务入库单位";

    this.quantityUnitTargets.forEach((target) => {
      target.textContent = unitLabel;
    });
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  // 筛选账号列表（带防抖）
  filterAccounts(event) {
    // 清除之前的定时器
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // 设置新的防抖延迟
    this.searchTimeout = setTimeout(() => {
      const searchTerm = event.target.value.toLowerCase();
      let visibleCount = 0;

      this.accountItemTargets.forEach((item) => {
        const credential = item.dataset.accountCredential;
        const matches = credential.includes(searchTerm);
        item.style.display = matches ? "" : "none";
        if (matches) visibleCount++;
      });

      // 显示搜索结果数量
      console.log(`搜索结果：${visibleCount} 个账号`);
    }, 200); // 200ms 防抖延迟
  }

  // 全选账号
  selectAllAccounts(event) {
    event.preventDefault();
    this.accountCheckboxTargets.forEach((checkbox) => {
      const item = checkbox.closest(
        '[data-mark-result-form-target="accountItem"]',
      );
      if (item.style.display !== "none") {
        checkbox.checked = true;
      }
    });
    this.updateAccountCount();
  }

  // 取消全选
  deselectAllAccounts(event) {
    event.preventDefault();
    this.accountCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateAccountCount();
  }

  // 更新已选择账号数量并显示/隐藏输入区域
  updateAccountCount() {
    if (!this.hasAccountCountTarget) return;

    const checkedBoxes = this.accountCheckboxTargets.filter((cb) => cb.checked);
    const count = checkedBoxes.length;

    // 更新计数显示
    const svgIcon = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
    </svg>`;
    this.accountCountTarget.innerHTML = `${svgIcon} 已选择 ${count} 个账号`;

    // 遍历所有账号，显示或隐藏对应的输入区域
    this.accountCheckboxTargets.forEach((checkbox) => {
      const accountId = checkbox.value;
      const inputSection = document.getElementById(
        `account-inputs-${accountId}`,
      );
      if (inputSection) {
        inputSection.style.display = checkbox.checked ? "" : "none";
      }
    });

    // 更新提交按钮文本
    if (this.hasSubmitButtonTarget) {
      const buttonText =
        count > 0 ? `批量创建 (${count} 个账号)` : "批量创建标注结果";

      // 更新按钮文本（保留 SVG 图标）
      const svg = this.submitButtonTarget.querySelector("svg");
      this.submitButtonTarget.textContent = buttonText;
      if (svg) {
        this.submitButtonTarget.prepend(svg);
      }
    }
  }

  // 当数量输入变化时，自动更新分钟显示（已移除，因为每个账号独立）

  // 在提交前进行验证
  async submit(event) {
    // 验证必填字段
    if (!this.validateRequiredFields()) {
      event.preventDefault();
      return false;
    }

    // 验证至少选择一个账号
    const selectedCount = this.accountCheckboxTargets.filter(
      (cb) => cb.checked,
    ).length;
    if (selectedCount === 0) {
      event.preventDefault();
      alert("请至少选择一个账号");
      return false;
    }

    return true;
  }

  // 验证必填字段
  validateRequiredFields() {
    const taskInput = document.querySelector(
      'input[name="mark_result[task_id]"]',
    );
    const subTaskInput = document.querySelector(
      'input[name="mark_result[sub_task_id]"]',
    );

    // 检查任务
    if (!taskInput?.value?.trim()) {
      alert("⚠️ 请选择标注任务");
      taskInput?.focus();
      return false;
    }

    // 检查子任务 ID
    if (!subTaskInput?.value?.trim()) {
      alert("⚠️ 请填写子任务 ID");
      subTaskInput?.focus();
      return false;
    }

    // 检查是否至少选择了一个账号
    const checkedBoxes = this.accountCheckboxTargets.filter((cb) => cb.checked);
    if (checkedBoxes.length === 0) {
      alert("⚠️ 请至少选择一个账号");
      return false;
    }

    // 检查每个选中账号是否填写了工作量
    let hasEmptyFields = false;
    let emptyAccounts = [];

    checkedBoxes.forEach((checkbox) => {
      const accountId = checkbox.value;
      const quantityInput = document.querySelector(
        `input[name="mark_result[accounts][${accountId}][quantity]"]`,
      );

      if (!quantityInput?.value || parseFloat(quantityInput.value) <= 0) {
        hasEmptyFields = true;
        const accountLabel = checkbox
          .closest('[data-mark-result-form-target="accountItem"]')
          ?.textContent.trim();
        emptyAccounts.push(accountLabel || accountId);
      }
    });

    if (hasEmptyFields) {
      alert(
        `⚠️ 以下账号未填写工作量：\n${emptyAccounts.slice(0, 3).join("\n")}${emptyAccounts.length > 3 ? `\n...及其他 ${emptyAccounts.length - 3} 个账号` : ""}`,
      );
      return false;
    }

    // 确认提交
    const confirmMessage = `📝 即将创建 ${checkedBoxes.length} 条标注结果记录，确认提交？`;
    return confirm(confirmMessage);
  }

  // 开发辅助：填充示例数据
  fillExample(event) {
    event.preventDefault();

    const timestamp = Date.now();

    if (this.hasSubTaskIdTarget) {
      this.subTaskIdTarget.value = `sub_task_${timestamp}`;
    }

    if (this.hasUserSubTaskIdTarget) {
      this.userSubTaskIdTarget.value = `user_sub_task_${timestamp}`;
    }

    if (this.hasQuantityTarget) {
      this.quantityTarget.value = "120.5";
      this.updateQuantityDisplay();
    }
  }
}
